import type { Section } from '@common/formEngine/formEngine.types';
import type { IRecipient } from '@common/recipient';
import { Gender } from '@common/enums';
import { dependsOn } from '@common/formEngine/dependsOn';
import { imageAndPdfExtensions } from '@common/utils/fileExtensions';
import { getProcessActivityAssetsSchema, type ProcessActivityAsset } from '@common/validation/processActivityAsset';
import type { ProcessActivityProfessionalSummaryData } from '../../processActivityProfessionalSummaryData';
import type { HolterRequest } from './hollterRequestsSchemas';
import type {
    DiagnosisData,
    HolterAnamnesisData,
    HolterEcgData,
    HolterHistoryData,
    HolterReferralData,
    HolterRiskFactorsData,
    MedicationData,
} from './utils/utils';
import { getServiceOrganizationProviderSchema } from '../../serviceOrganizationProvider';
import {
    holterAnamnesisSection,
    holterHistorySection,
    holterRustEcgSection,
    medicationSection,
    professionalSummarySectionFields,
} from './hollterRequestsSchemas';
import { referralSectionBaseFields } from './holterExaminationRequestSchemas';
import {
    FamilyRiskFactorOption,
    familyRiskFactorOptions,
    FemaleRiskFactorOption,
    femaleRiskFactorOptions,
    GeneralRiskFactorOption,
    generalRiskFactorOptions,
    YesNoNoDeviations,
    yesNoNoDeviationsOptions,
} from './utils/utils';

export type HolterConsultRequestBase = {
    adviceQuestion: string | null;
    assets: ProcessActivityAsset[];
    recipient: IRecipient;
} & ProcessActivityProfessionalSummaryData &
    HolterRiskFactorsData &
    DiagnosisData;

export type HolterConsultRequestData = HolterConsultRequestBase &
    HolterReferralData &
    HolterHistoryData &
    HolterEcgData &
    HolterAnamnesisData &
    MedicationData;

export const referralSection: Section<HolterConsultRequestBase & HolterReferralData, undefined, HolterRequest> = {
    fields: [
        ...referralSectionBaseFields,
        {
            draftSchema: getServiceOrganizationProviderSchema(true),
            finalSchema: getServiceOrganizationProviderSchema(),
            initialValue: {
                organizationId: null,
                practitionerId: null,
            },
            key: 'recipient',
            label: 'Verstuur naar',
            type: 'custom',
        },
    ],
};

export const examinationSection: Section<HolterConsultRequestData, undefined, HolterRequest> = {
    fields: [
        {
            acceptExtensions: imageAndPdfExtensions,
            draftSchema: getProcessActivityAssetsSchema(),
            finalSchema: getProcessActivityAssetsSchema({ min: 1 }),
            isRequired: true,
            key: 'assets',
            label: 'Onderzoek',
            type: 'assets',
        },
    ],
    title: 'Onderzoek',
};

export const adviceQuestionSection: Section<HolterConsultRequestBase> = {
    fields: [
        {
            isRequired: true,
            key: 'adviceQuestion',
            label: 'Vraag aan de cardioloog',
            type: 'textArea',
        },
    ],
    title: 'Adviesvraag',
};

export const holterRiskFactorsSection: Section<HolterRiskFactorsData, undefined, HolterRequest> = {
    fields: [
        {
            checkboxEnumType: GeneralRiskFactorOption,
            isRequired: false,
            key: 'general_riskfactors',
            label: 'Algemene risicofactoren',
            type: 'nestedCheckboxGroup',
            valueOptions: generalRiskFactorOptions,
        },
        {
            checkboxEnumType: FemaleRiskFactorOption,
            include: dependsOn(['patientData.gender'], (data) => (data.patientData.gender !== Gender.male) === true),
            initialValue: {
                hypertension_before_menopause: false,
                hypertension_during_pregnancy: false,
                menopause: false,
                miscarriage: false,
            },
            isRequired: false,
            key: 'female_riskfactors',
            label: 'Vrouwelijke risicofactoren',
            type: 'checkboxGroup',
            valueOptions: femaleRiskFactorOptions,
        },
        {
            checkboxEnumType: FamilyRiskFactorOption,
            isRequired: false,
            key: 'familial_riskfactors',
            label: 'Familiaire risicofactoren',
            type: 'checkboxGroup',
            valueOptions: familyRiskFactorOptions,
        },
        {
            key: 'riskfactors_remark',
            label: 'Toelichting risicofactoren',
            type: 'textArea',
        },
    ],
    title: 'Risicofactoren',
};

export const diagnosisSection: Section<DiagnosisData> = {
    fields: [
        {
            isRequired: true,
            key: 'hasDiagnosis',
            label: 'Diagnose bekend',
            layout: 'row',
            type: 'radioGroup',
            valueOptions: yesNoNoDeviationsOptions,
        },

        {
            include: dependsOn('hasDiagnosis', ({ hasDiagnosis }) => hasDiagnosis === YesNoNoDeviations.yes),
            isRequired: true,
            key: 'diagnosis_icd10',
            label: 'Werkdiagnose',
            type: 'icd10',
        },
    ],
    title: 'Diagnose',
};

export const professionalSummarySection: Section<ProcessActivityProfessionalSummaryData, undefined, HolterRequest> = {
    fields: professionalSummarySectionFields,
    title: 'Professionele samenvatting',
};

export const holterConsultRequestSections: Section<HolterConsultRequestData, undefined, HolterRequest>[] = [
    referralSection,
    adviceQuestionSection,
    examinationSection,
    holterAnamnesisSection,
    holterRustEcgSection,
    holterHistorySection,
    medicationSection,
    holterRiskFactorsSection,
    professionalSummarySection,
    diagnosisSection,
];
